import React, { useCallback } from 'react';

import { SearchCombobox } from './SearchCombobox';

/**
 * A dropdown that searches an API endpoint as you type.
 *
 * Provides a searchable combobox that fetches options from your backend
 * based on user input. Includes debouncing, loading states, and keyboard navigation.
 *
 * Perfect for selecting from large datasets like providers where you can't
 * load everything upfront.
 */
const AsyncSearchCombobox = React.forwardRef(
  /**
   * @param {Object} props
   * @param {React.Ref<HTMLInputElement>} ref
   */
  (props, ref) => {
    const {
      value,
      onChange,
      label = '',
      placeholder = 'Search…',
      disabled = false,
      debounceWait = 500,
      emptyText = 'No results',
      showError = false,
      fetchOptionsFunction,
      getInputDisplayValue,
      getOptionLabel,
      getOptionKey,
      name,
    } = props;

    const getInputDisplayValueFallback = useCallback(() => {
      return value;
    }, [value]);

    return (
      <SearchCombobox
        value={value}
        onChange={onChange}
        label={label}
        placeholder={placeholder}
        disabled={disabled}
        debounceWait={debounceWait}
        emptyText={emptyText}
        showError={showError}
        fetchOptionsFunction={fetchOptionsFunction}
        getInputDisplayValue={
          getInputDisplayValue || getInputDisplayValueFallback
        }
        getOptionLabel={getOptionLabel}
        getOptionKey={getOptionKey}
        onFocus={undefined}
        customOnChange={undefined}
        CustomOptions={undefined}
        name={name}
        ref={ref}
      />
    );
  },
);

export default AsyncSearchCombobox;
